import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { filter, first } from 'rxjs/operators';

import { environment } from 'environments/environment';

@Injectable()
export class MetaInfoService {
    private title = inject(Title);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private translateService = inject(TranslateService);
    private meta = inject(Meta);

    private parsedTitle = '';
    private separator = ' - ';
    private unparsedTitle = '';

    init(): void {
        // No error handler needed: onLangChange and router.events are internal
        // streams, not network calls, and do not error.
        combineLatest(
            this.translateService.onLangChange,
            this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
        ).subscribe(() => this.parseTitle());
    }

    private parseTitle(): void {
        const titleParts = [];
        let route = this.activatedRoute.snapshot.root;
        let description = 'global.meta_description';

        if (route.data['title']) {
            titleParts.push(route.data['title']);
        }

        while (route.firstChild) {
            route = route.firstChild;
            if (route.data['title'] && titleParts.indexOf(route.data['title']) === -1) {
                titleParts.push(route.data['title']);
            }

            description = route.data['description'] || description;
        }

        this.handleDescriptionChange(description);
        this.handleTitleChange(titleParts);
    }

    replaceTitle(title: string, titleValue: string): void {
        const titleIndex = this.unparsedTitle.split(this.separator).indexOf(title);
        const currentTitle = this.parsedTitle;
        const titleParts = currentTitle.split(this.separator);
        titleParts[titleIndex] = titleValue;
        this.setPageTitle(this.joinParts(titleParts));
    }

    private handleDescriptionChange(description: string): void {
        this.meta.updateTag({ name: 'description', content: this.translateService.instant(description) });
    }

    private handleTitleChange(titleParts: string[]): void {
        if (!titleParts.length) {
            this.unparsedTitle = this.joinParts([]);
            this.setPageTitle(this.joinParts([]));
            return;
        }

        // No error handler needed: TranslateService.get() resolves from already-loaded
        // in-memory translations, not a network call, and does not error in practice.
        this.translateService
            .get(titleParts)
            .pipe(first())
            .subscribe((translations: { [key: string]: string }) => {
                const translatedParts = Object.values(translations);

                const oldParts = this.unparsedTitle.split(this.separator);
                const currentParts = this.parsedTitle.split(this.separator);

                const newParts = translatedParts.map((part, index) => {
                    if (index < oldParts.length && part === oldParts[index]) {
                        return currentParts[index];
                    }
                    return part;
                });

                this.unparsedTitle = this.joinParts(translatedParts);
                this.setPageTitle(this.joinParts(newParts));
            });
    }

    private joinParts(titleParts: string[]): string {
        const joinedTitle = titleParts.join(this.separator).trim();
        return joinedTitle;
    }

    private setPageTitle(title: string): void {
        const appName = environment.appName;
        title = title || appName;

        this.parsedTitle = title;

        const regex = new RegExp(/{{.+?(?=}})}}/);

        if (regex.test(title)) {
            title = appName;
        }

        this.title.setTitle(title);
    }
}
